/**
 * ImportButton Component
 *
 * Standalone import button for data import across all tables.
 * Always enabled regardless of row selection state.
 *
 * Usage:
 * ```tsx
 * <ImportButton onClick={() => setShowImportModal(true)} />
 * ```
 *
 * @module components/actions
 */

import { ArrowDownOnSquareIcon } from '@heroicons/react/24/outline';

import { Button } from '@/components/Button';

export interface ImportButtonProps {
  /** Handler for import button click */
  onClick: () => void;
  /** Disable the button (default: false - always enabled) */
  disabled?: boolean;
  /** Additional CSS classes */
  className?: string;
  /** Test ID for E2E testing */
  'data-testid'?: string;
}

/**
 * ImportButton Component
 *
 * Always-enabled import button that matches the visual style of action dropdowns.
 * Designed to work independently of row selection state.
 *
 * Uses true Button component for direct 1-click interaction.
 *
 * @param props - Component props
 * @returns Import button JSX
 *
 * @example
 * ```tsx
 * <ImportButton
 *   onClick={() => setShowImportModal(true)}
 *   data-testid="vulnerability-import-button"
 * />
 * ```
 */
export function ImportButton({
  onClick,
  disabled = false,
  className,
  'data-testid': testId = 'import-button',
}: ImportButtonProps) {
  return (
    <Button
      onClick={onClick}
      disabled={disabled}
      styleType="rounded"
      size="xxs"
      startIcon={<ArrowDownOnSquareIcon className="size-4" />}
      className={className}
      data-testid={testId}
      aria-label="Import data"
    >
      Import
    </Button>
  );
}
